import { PaymentMethod } from "../../../model/paymentMethod";
import { HStack } from "../../Stack";
import { ReactComponent as CreditCardIcon } from "images/credit_card.svg";
import { Radio, RadioGroup } from "@material-ui/core";
import { useStyles } from "./const";
import { ReactNode, useRef, useState } from "react";
import classnames from "classnames";
import {
  generateIdempotencyKey,
  postPricingModelItemPurchaseIntentAsync,
} from "../../../services/pricingmodels";
import { PricingModelItem } from "../../../model/pricingmodel";
import { logError } from "../../../services/logging";
import TalaeraSpinner from "../../TalaeraSpinner/TalaeraSpinner";
import Submit from "../Submit";
import PaymentSucceedModal from "../AddNewPaymentMethod/PaymentSucceedModal";
import PaymentFailedModal from "../AddNewPaymentMethod/PaymentFailedModal";
import { Stripe } from "@stripe/stripe-js";

function PaymentMethodRow({
  paymentMethod,
  isSelected,
}: {
  paymentMethod: PaymentMethod;
  isSelected: boolean;
}) {
  const classes = useStyles();
  return (
    <HStack justify="space-between" className={classnames({ active: isSelected })}>
      <HStack align="center" gap="8px">
        <CreditCardIcon />
        <span>•••• •••• •••• {paymentMethod.card.last4}</span>
      </HStack>
      <Radio
        checked={isSelected}
        className={classes.root}
        disableRipple
        color="default"
        checkedIcon={<span className={`${classes.icon} ${classes.checkedIcon}`} />}
        icon={<span className={classes.icon} />}
      />
    </HStack>
  );
}

interface Props {
  paymentMethods: PaymentMethod[];
  pricingModelItem: PricingModelItem;
  onSuccess: () => void;
  stripePromise: Promise<Stripe | null>;
  reload: () => void;
  secondaryPaymentAction?: ReactNode;
}

export default function ExistingPaymentMethods({
  paymentMethods,
  pricingModelItem,
  onSuccess,
  stripePromise,
  reload,
  secondaryPaymentAction,
}: Props) {
  const [selected, setSelected] = useState<PaymentMethod>(paymentMethods[0]);
  const [idempotencyKey, setIdempotencyKey] = useState(generateIdempotencyKey());
  const blockUI = useRef(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [showModalDialog, setShowModalDialog] = useState<"success" | "failed" | null>(
    null
  );

  const updateIdempotencyKey = () => {
    setIdempotencyKey(generateIdempotencyKey());
  };

  const hdOnChange = (evt: any) => {
    evt.preventDefault();

    const id = evt.target.value;
    const newSelection = paymentMethods.find(pm => pm.id === id);
    newSelection && setSelected(newSelection);
  };

  const hdOnSubmit = async (evt: any) => {
    evt.preventDefault();
    if (blockUI.current) {
      return;
    }
    blockUI.current = true;
    setLoading(true);
    let resp;
    try {
      resp = await postPricingModelItemPurchaseIntentAsync({
        id: pricingModelItem.id!,
        idempotencyKey,
        use_saved_pm: true,
      });
    } catch (e) {
      const error = e as any;
      logError("Failed to purchase pricing model with saved card due to error", {
        pricingModelItem,
        error: e,
      });
      setLoading(false);
      blockUI.current = false;
      if (error?.statusCode === 400) {
        // This idempotency key was already used and failed.
        updateIdempotencyKey();
        reload();
        return;
      } else {
        if (error?.statusCode === 402) {
          // This idempotency key has failed.
          updateIdempotencyKey();
        }
        setShowModalDialog("failed");
      }
      return;
    }

    if (resp.status === "processing") {
      let actionResult;
      try {
        const stripe = await stripePromise;
        if (!stripe) {
          setShowModalDialog("failed");
        }
        actionResult = await stripe!.confirmCardPayment(resp.client_secret);
        setShowModalDialog("success");
      } catch (e) {
        // That's just a network error most likely.
        setShowModalDialog("failed");
        blockUI.current = false;
        setLoading(false);
        return;
      }

      if (actionResult.error) {
        // Here it is an unrecoverable error that requires update of idempotency key.
        updateIdempotencyKey();
        setShowModalDialog("failed");
      } else {
        setShowModalDialog("success");
      }
    } else {
      setShowModalDialog("success");
    }
    blockUI.current = false;
    setLoading(false);
  };

  return (
    <div>
      {loading && <TalaeraSpinner />}
      {showModalDialog === "success" && (
        <PaymentSucceedModal plan={pricingModelItem} onClose={onSuccess} />
      )}
      {showModalDialog === "failed" && (
        <PaymentFailedModal onClose={() => setShowModalDialog(null)} />
      )}
      <RadioGroup defaultValue={selected.id} value={selected.id} onChange={hdOnChange}>
        {paymentMethods.map(pm => (
          <PaymentMethodRow
            key={pm.id}
            paymentMethod={pm}
            isSelected={pm.id === selected.id}
          />
        ))}
      </RadioGroup>
      <Submit pricingItem={pricingModelItem} hdSubmit={hdOnSubmit}>
        {secondaryPaymentAction}
      </Submit>
    </div>
  );
}
